import { RefObject, useRef } from 'react';

/**
 * Returns a stable ref that always holds the latest value of the given variable.
 *
 * The ref object itself never changes identity between renders, making it safe
 * to include in `useCallback`/`useEffect` dependency arrays instead of the
 * raw value. This avoids unnecessary re-creations of callbacks that only read
 * the value — they can read `ref.current` at call-time and always get the
 * up-to-date value without being listed as a dependency.
 *
 * @example
 * // Without useLatest, changing `view` recreates `handlePress` on every render,
 * // which causes every memoized child that receives it as a prop to re-render too.
 * const handlePress = useCallback(() => {
 *   if (view === 'accounts') { ... }
 * }, [view]); // ← `view` in deps → new reference on every state change
 *
 * // With useLatest, the callback is created once and reads the latest view via ref.
 * const latestView = useLatest(view);
 * const handlePress = useCallback(() => {
 *   if (latestView.current === 'accounts') { ... }
 * }, [latestView]); // ← stable ref → callback identity never changes
 */
export const useLatest = <T>(value: T): RefObject<T> => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};
